import React, { useEffect, useMemo, useRef, useState } from "react";
import { IconArrowLeft, IconArrowRight, IconFilter, IconSearch, IconX } from "@tabler/icons-react";
import {
  useEncoderAhaSearch,
  useEncoderCmIndexSearch,
  useEncoderCmIndexTree,
  useEncoderCmTable,
  useEncoderCmTabular,
  useEncoderCmTabularSearch,
  useEncoderCmYears,
  useEncoderIcdGuidelineSearch,
} from "../../hooks/queries";
import type {
  CmIndexTermNode,
  CmSearchHit,
  CmSearchSource,
  CmTableTermNode,
  CmTabularNoteHit,
  CmTabularSearchMode,
} from "../../types/encoder";
import { cn } from "../../utils/encoder";
import {
  type CmDeepSearchHit,
  type ExpandState,
  CM_SOURCE_OPTIONS,
  applySeventhCharacter,
  buildCmReferenceKeys,
  cleanRedirectTerm,
  codedSuggestionLine,
  collectDeepSearchHits,
  filterLineageTree,
  formatCmCode,
  isCmIndexSource,
  isCmTableSource,
  leftoverIndexWords,
  scoreCmLeadHit,
  rankAhaResultsByCmCode,
  rankCmGuidelines,
  sectionLetterOf,
  seventhCharLookup,
  stripIncompleteSuffix,
  textMatchesSearchWord,
  tokenizeQuery,
  treeContainsSearchWord,
  uniqueWords,
} from "../../utils/encoder-cm";
import { useRegisterCmChromeNav } from "./context";
import {
  AlphabetJumpBar,
  CmEmptyState,
  CodedTermSuggestion,
  HighlightedText,
  IndexTreeView,
  TableTreeView,
  TabularListSearch,
  TabularPanel,
} from "./CmBuilderViews";
import {
  type CmReferenceTab,
  CmCodingReferenceDialog,
  CmCodingReferencePanel,
} from "./CmCodingReference";
import { Spinner, useDebouncedValue } from "./shared";

type NavEntry = {
  source: CmSearchSource;
  query: string;
  termId: string | null;
  refine: string;
  code: string | null;
};

const DEFAULT_EXPAND: ExpandState = {
  mode: "all",
  overrides: {},
  defaultDepth: 1,
};

const CmBuilderTab: React.FC<{
  onConfirmCode?: (payload: { code: string; description: string }) => void;
}> = ({ onConfirmCode }) => {
  const yearsQuery = useEncoderCmYears();
  const years: number[] = yearsQuery.data?.years?.length
    ? yearsQuery.data.years
    : [2026, 2025, 2024, 2023, 2022, 2021];
  const [year, setYear] = useState(yearsQuery.data?.defaultYear ?? 2026);
  const [source, setSource] = useState<CmSearchSource>("index");
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [refineInput, setRefineInput] = useState("");
  const [refine, setRefine] = useState("");
  const [refineOpen, setRefineOpen] = useState(false);
  const [selectedTermId, setSelectedTermId] = useState<string | null>(null);
  const [indexMenuOpen, setIndexMenuOpen] = useState(false);
  const [past, setPast] = useState<NavEntry[]>([]);
  const [future, setFuture] = useState<NavEntry[]>([]);
  const [requestedCode, setRequestedCode] = useState<string | null>(null);
  const [tabularPast, setTabularPast] = useState<string[]>([]);
  const [selectedSeventh, setSelectedSeventh] = useState<string | null>(null);
  const [tabularSearchMode, setTabularSearchMode] =
    useState<CmTabularSearchMode>("code");
  const [tabularSearchInput, setTabularSearchInput] = useState("");
  const [tabularSearchOpen, setTabularSearchOpen] = useState(false);
  const [expand, setExpand] = useState<ExpandState>(DEFAULT_EXPAND);
  const [jumpSectionId, setJumpSectionId] = useState<string | null>(null);
  const [jumpedIndexTermId, setJumpedIndexTermId] = useState<string | null>(null);
  const [referenceTab, setReferenceTab] = useState<CmReferenceTab>("guidelines");
  const [referenceOpen, setReferenceOpen] = useState(false);
  const [openGuidelineIds, setOpenGuidelineIds] = useState<number[]>([]);
  const [openAhaIds, setOpenAhaIds] = useState<number[]>([]);

  const searchInputRef = useRef<HTMLTextAreaElement | null>(null);
  const indexSearchRef = useRef<HTMLDivElement | null>(null);
  const treeScrollRef = useRef<HTMLDivElement | null>(null);
  const selectionTextRef = useRef<string | null>(null);
  const refineSelectionTextRef = useRef<string | null>(null);

  const tableMode = isCmTableSource(source);
  const indexSource = isCmIndexSource(source) ? source : "index";
  const tableSource = source === "drugs" ? "drugs" : "neoplasm";
  const tableFilter =
    tableMode && source === "drugs" && query.trim().length >= 2
      ? query.trim()
      : "";
  const tabularCode = requestedCode
    ? stripIncompleteSuffix(requestedCode)
    : null;
  const debouncedTabular = useDebouncedValue(tabularSearchInput, 250);
  const hasQuery = query.trim().length >= 2;
  const tabularMinLength = tabularSearchMode === "code" ? 1 : 2;

  useEffect(() => {
    const field = searchInputRef.current;
    if (!field) return;
    field.style.height = "32px";
    if (!searchInput) return;
    field.style.height = `${Math.max(field.scrollHeight, 32)}px`;
  }, [searchInput]);

  useEffect(() => {
    if (selectionTextRef.current === searchInput) return;
    selectionTextRef.current = null;
    const timer = window.setTimeout(() => setQuery(searchInput.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (refineSelectionTextRef.current === refineInput) return;
    refineSelectionTextRef.current = null;
    const timer = window.setTimeout(() => setRefine(refineInput.trim()), 150);
    return () => window.clearTimeout(timer);
  }, [refineInput]);

  useEffect(() => {
    if (!indexMenuOpen) return;
    const dismiss = (event: MouseEvent) => {
      if (!indexSearchRef.current?.contains(event.target as Node)) {
        setIndexMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", dismiss);
    return () => document.removeEventListener("mousedown", dismiss);
  }, [indexMenuOpen]);

  useEffect(() => {
    const available = yearsQuery.data?.years ?? [];
    if (!available.length) return;
    if (available.indexOf(year) >= 0) return;
    const preferred = yearsQuery.data?.defaultYear ?? available[0];
    setYear(available.indexOf(preferred) >= 0 ? preferred : available[0]);
  }, [yearsQuery.data, year]);

  const search = useEncoderCmIndexSearch(
    year,
    indexSource,
    tableMode ? "" : query
  );
  const results: CmSearchHit[] = useMemo(() => {
    const list = search.data?.results ?? [];
    const words = search.data?.queryWords?.length
      ? search.data.queryWords
      : tokenizeQuery(query);
    return list.slice().sort(
      (left: CmSearchHit, right: CmSearchHit) =>
        scoreCmLeadHit(right, words) - scoreCmLeadHit(left, words)
    );
  }, [search.data, query]);
  const tabularNoteHits: CmTabularNoteHit[] = useMemo(
    () => search.data?.tabularNotes ?? [],
    [search.data]
  );

  useEffect(() => {
    if (query.length < 2) setSelectedTermId(null);
  }, [query]);

  useEffect(() => {
    if (!search.data) return;
    setSelectedTermId((current) => {
      if (selectionTextRef.current && current) return current;
      return results[0]?.id ?? null;
    });
  }, [search.data, query, results]);

  const selectedHit = useMemo(
    () => results.find((hit) => hit.id === selectedTermId) ?? null,
    [results, selectedTermId]
  );

  useEffect(() => {
    refineSelectionTextRef.current = null;
    setRefineInput("");
    setRefine("");
    setRefineOpen(false);
  }, [selectedTermId]);

  const treeQuery = useEncoderCmIndexTree(
    year,
    indexSource,
    tableMode ? null : selectedTermId
  );
  const tableQuery = useEncoderCmTable(
    year,
    tableSource,
    tableFilter,
    tableMode
  );
  const tabularQuery = useEncoderCmTabular(year, tabularCode);
  const tabularSearch = useEncoderCmTabularSearch(
    year,
    tabularSearchMode,
    debouncedTabular
  );

  const rawIndexTree = treeQuery.data?.tree ?? null;
  const tabular = tabularQuery.data ?? null;
  const queryWords = useMemo(
    () =>
      uniqueWords(
        search.data?.queryWords?.length
          ? search.data.queryWords
          : tokenizeQuery(query)
      ),
    [search.data?.queryWords, query]
  );
  const leadFilterWords = useMemo(() => {
    if (tableMode || !selectedHit) return [];
    const leftover = leftoverIndexWords(
      selectedHit.title,
      selectedHit.nemod,
      queryWords
    );
    if (!rawIndexTree) return leftover;
    const children = rawIndexTree.children || [];
    const direct = leftover.filter((word: string) =>
      children.some((child: CmIndexTermNode) =>
        textMatchesSearchWord(`${child.title} ${child.nemod}`, word)
      )
    );
    if (direct.length > 0) return direct;
    return leftover.filter((word: string) =>
      treeContainsSearchWord(rawIndexTree, word)
    );
  }, [tableMode, selectedHit, queryWords, rawIndexTree]);
  const filterWords = useMemo(() => {
    const typed = refine.trim().length >= 2 ? tokenizeQuery(refine) : [];
    return uniqueWords(leadFilterWords.concat(typed));
  }, [leadFilterWords, refine]);
  const highlightWords = useMemo(
    () => uniqueWords(queryWords.concat(filterWords)),
    [queryWords, filterWords]
  );
  const indexTree = useMemo(() => {
    if (!rawIndexTree) return null;
    if (filterWords.length === 0) return rawIndexTree;
    return filterLineageTree(rawIndexTree, filterWords);
  }, [rawIndexTree, filterWords]);

  useEffect(() => {
    if (tableMode || selectionTextRef.current) return;
    if (!rawIndexTree || !selectedHit || queryWords.length === 0) return;
    const leftover = leftoverIndexWords(
      selectedHit.title,
      selectedHit.nemod,
      queryWords
    );
    if (leftover.length === 0) return;
    const covered = leftover.some((word: string) =>
      treeContainsSearchWord(rawIndexTree, word)
    );
    if (covered) return;
    const next = results.find((hit: CmSearchHit) => {
      const title = hit.title.toLowerCase().trim();
      return leftover.indexOf(title) >= 0;
    });
    if (next && next.id !== selectedHit.id) {
      setSelectedTermId(next.id);
    }
  }, [tableMode, rawIndexTree, selectedHit, queryWords, results]);
  const tableFilterWords = useMemo(
    () => (tableMode && query.trim().length >= 2 ? tokenizeQuery(query) : []),
    [tableMode, query]
  );
  const tableTrees = useMemo(() => {
    const trees = tableQuery.data?.trees ?? [];
    if (tableFilterWords.length === 0) return trees;
    return trees
      .map((tree: CmTableTermNode) =>
        filterLineageTree(tree, tableFilterWords)
      )
      .filter((tree: CmTableTermNode | null): tree is CmTableTermNode =>
        Boolean(tree)
      );
  }, [tableQuery.data?.trees, tableFilterWords]);

  const deepSearchHits = useMemo(() => {
    if (!rawIndexTree) return [];
    return collectDeepSearchHits(rawIndexTree, filterWords);
  }, [rawIndexTree, filterWords]);

  const indexSuggestionHits = useMemo(() => {
    if (tableMode || !rawIndexTree || !selectedTermId) return [];
    if (!results.some((hit) => hit.id === selectedTermId)) return [];
    return collectDeepSearchHits(rawIndexTree, queryWords);
  }, [tableMode, rawIndexTree, selectedTermId, results, queryWords]);

  const leftoverSuggestionWords = useMemo(() => {
    if (!selectedHit) return queryWords.slice(1);
    return leftoverIndexWords(selectedHit.title, selectedHit.nemod, queryWords);
  }, [queryWords, selectedHit]);

  const codedMatchesFirst =
    leftoverSuggestionWords.length > 0 && indexSuggestionHits.length > 0;

  const seventhCharCodes = useMemo(
    () =>
      seventhCharLookup(
        tableMode
          ? tableQuery.data?.seventhCharCodes
          : treeQuery.data?.seventhCharCodes
      ),
    [tableMode, tableQuery.data?.seventhCharCodes, treeQuery.data?.seventhCharCodes]
  );

  const sectionLetters = useMemo(() => {
    const letters: Record<string, true> = {};
    const children = indexTree?.children ?? [];
    for (let i = 0; i < children.length; i += 1) {
      const letter = sectionLetterOf(children[i].title);
      if (letter) letters[letter] = true;
    }
    return letters;
  }, [indexTree]);

  const currentNav = (): NavEntry => ({
    source,
    query: searchInput,
    termId: selectedTermId,
    refine: refineInput,
    code: requestedCode,
  });

  const pushHistory = () => {
    setPast((prev) => prev.concat([currentNav()]));
    setFuture([]);
  };

  const applyNav = (entry: NavEntry) => {
    selectionTextRef.current = entry.query;
    refineSelectionTextRef.current = entry.refine;
    setSource(entry.source);
    setSearchInput(entry.query);
    setQuery(entry.query.trim());
    setSelectedTermId(entry.termId);
    setRefineInput(entry.refine);
    setRefine(entry.refine.trim());
    setRequestedCode(entry.code);
    setSelectedSeventh(null);
    setExpand(DEFAULT_EXPAND);
  };

  const goIndexBack = () => {
    if (past.length === 0) return;
    const previous = past[past.length - 1];
    setPast((prev) => prev.slice(0, -1));
    setFuture((prev) => [currentNav()].concat(prev));
    applyNav(previous);
  };

  const goIndexForward = () => {
    if (future.length === 0) return;
    const next = future[0];
    setFuture((prev) => prev.slice(1));
    setPast((prev) => prev.concat([currentNav()]));
    applyNav(next);
  };

  useRegisterCmChromeNav({
    canGoBack: past.length > 0,
    canGoForward: future.length > 0,
    onBack: goIndexBack,
    onForward: goIndexForward,
  });

  const pinSearchLabel = (label: string) => {
    selectionTextRef.current = label;
    setSearchInput(label);
  };

  const pinRefineLabel = (label: string) => {
    refineSelectionTextRef.current = label;
    setRefineInput(label);
  };

  const openTabular = (
    code: string,
    options?: { termId?: string; fromTabularSearch?: boolean }
  ) => {
    if (
      requestedCode &&
      stripIncompleteSuffix(requestedCode) !== stripIncompleteSuffix(code)
    ) {
      setTabularPast((prev) => prev.concat([requestedCode]));
    }
    setRequestedCode(code);
    setSelectedSeventh(null);
    if (options?.fromTabularSearch) {
      setTabularSearchOpen(false);
      return;
    }
    if (options?.termId) setJumpedIndexTermId(options.termId);
  };

  const resetTabularValidation = () => {
    setRequestedCode(null);
    setTabularPast([]);
    setSelectedSeventh(null);
    setTabularSearchInput("");
    setTabularSearchOpen(false);
  };

  const goTabularBack = () => {
    if (tabularPast.length === 0) return;
    const previous = tabularPast[tabularPast.length - 1];
    setTabularPast((prev) => prev.slice(0, -1));
    setRequestedCode(previous);
    setSelectedSeventh(null);
  };

  const selectTerm = (hit: CmSearchHit) => {
    setIndexMenuOpen(false);
    if (hit.id !== selectedTermId) {
      pushHistory();
      setSelectedTermId(hit.id);
    }
    pinSearchLabel(
      codedSuggestionLine({
        id: hit.id,
        title: hit.title,
        nemod: hit.nemod,
        code: hit.code ?? "",
        path: [],
      })
    );
    setExpand(DEFAULT_EXPAND);
    if (hit.code) openTabular(hit.code);
  };

  const selectCodedSuggestion = (hit: CmDeepSearchHit) => {
    setIndexMenuOpen(false);
    pinSearchLabel(codedSuggestionLine(hit));
    openTabular(hit.code, { termId: hit.id });
  };

  const selectTabularNote = (hit: {
    id: string;
    title: string;
    code: string;
    noteType: "includes" | "inclusionTerm";
  }) => {
    setIndexMenuOpen(false);
    pinSearchLabel(hit.title);
    openTabular(hit.code);
  };

  const selectDeepSearchHit = (hit: CmDeepSearchHit) => {
    pushHistory();
    pinRefineLabel(codedSuggestionLine(hit));
    openTabular(hit.code, { termId: hit.id });
    setRefineOpen(false);
  };

  const followCrossRef = (label: string, rawTerm: string) => {
    const parsed = cleanRedirectTerm(rawTerm);
    const target =
      isCmTableSource(source) && !isCmTableSource(parsed.source)
        ? source
        : parsed.source;
    pushHistory();
    setSource(target);
    setIndexMenuOpen(false);
    setRefineInput("");
    setRefine("");
    if (isCmTableSource(target)) {
      setSelectedTermId(null);
      pinSearchLabel(parsed.cleanTerm);
      setQuery(parsed.cleanTerm);
    } else {
      pinSearchLabel(parsed.cleanTerm);
      setQuery(parsed.cleanTerm);
      setSelectedTermId(null);
    }
    void label;
  };

  const changeSource = (next: CmSearchSource) => {
    if (next === source) return;
    pushHistory();
    setSource(next);
    setSelectedTermId(null);
    setIndexMenuOpen(false);
    setJumpSectionId(null);
    setExpand(DEFAULT_EXPAND);
  };

  const clearSearch = () => {
    selectionTextRef.current = null;
    setSearchInput("");
    setQuery("");
    setSelectedTermId(null);
    setIndexMenuOpen(false);
  };

  const jumpToLetter = (letter: string) => {
    const children = indexTree?.children ?? [];
    const match = children.find(
      (child: CmIndexTermNode) => sectionLetterOf(child.title) === letter
    );
    if (!match) return;
    setJumpSectionId(match.id);
    const root = treeScrollRef.current;
    const target = root?.querySelector(
      `[data-cm-tree-term="${match.id}"]`
    ) as HTMLElement | null;
    target?.scrollIntoView({ block: "start" });
  };

  const toggleNode = (id: string, next: boolean) => {
    setExpand((current) => ({
      ...current,
      overrides: { ...current.overrides, [id]: next },
    }));
  };

  const finalizedCode = useMemo(() => {
    if (!tabular) return null;
    if (tabular.needsSeventhChar && selectedSeventh) {
      return applySeventhCharacter(tabular.cleanCode || tabular.code, selectedSeventh);
    }
    if (tabular.isBillable) return formatCmCode(stripIncompleteSuffix(tabular.code));
    return null;
  }, [tabular, selectedSeventh]);

  const canCopyCode = Boolean(
    tabular &&
      tabular.isBillable &&
      (!tabular.needsSeventhChar || selectedSeventh)
  );

  const referenceKeys = useMemo(
    () => buildCmReferenceKeys(finalizedCode ?? tabular?.code ?? null),
    [finalizedCode, tabular?.code]
  );
  const hasCategoryLookup = Boolean(
    referenceKeys && referenceKeys.category !== referenceKeys.code
  );

  const guidelineByCode = useEncoderIcdGuidelineSearch({
    guidelineType: "ICD-10-CM",
    keyword: referenceKeys?.code,
    enabled: Boolean(referenceKeys),
  });
  const guidelineByCategory = useEncoderIcdGuidelineSearch({
    guidelineType: "ICD-10-CM",
    keyword: referenceKeys?.category,
    enabled: Boolean(referenceKeys) && hasCategoryLookup,
  });
  const ahaByCode = useEncoderAhaSearch({
    keyword: referenceKeys?.code,
    cmContext: true,
    referenceCode: referenceKeys?.code,
    referenceCategory: referenceKeys?.category,
    enabled: Boolean(referenceKeys),
  });
  const ahaByCategory = useEncoderAhaSearch({
    keyword: referenceKeys?.category,
    cmContext: true,
    referenceCode: referenceKeys?.code,
    referenceCategory: referenceKeys?.category,
    enabled: Boolean(referenceKeys) && hasCategoryLookup,
  });

  const cmGuidelines = useMemo(() => {
    if (!referenceKeys) return [];
    return rankCmGuidelines(
      (guidelineByCode.data ?? []).concat(guidelineByCategory.data ?? []),
      referenceKeys
    );
  }, [referenceKeys, guidelineByCode.data, guidelineByCategory.data]);

  const cmAhaResults = useMemo(() => {
    if (!referenceKeys) return [];
    return rankAhaResultsByCmCode(
      (ahaByCode.data ?? []).concat(ahaByCategory.data ?? []),
      referenceKeys
    );
  }, [referenceKeys, ahaByCode.data, ahaByCategory.data]);

  const toggleGuideline = (id: number) => {
    setOpenGuidelineIds((current) =>
      current.indexOf(id) >= 0
        ? current.filter((item) => item !== id)
        : current.concat([id])
    );
  };
  const toggleAha = (id: number) => {
    setOpenAhaIds((current) =>
      current.indexOf(id) >= 0
        ? current.filter((item) => item !== id)
        : current.concat([id])
    );
  };

  const searchPlaceholder = tableMode
    ? source === "drugs"
      ? "Search a substance (e.g. aspirin)…"
      : "Search a site within the neoplasm table…"
    : "Search a lead term (e.g. fracture, diabetes)…";

  const leftPanelLoading = tableMode
    ? tableQuery.isFetching && tableTrees.length === 0
    : selectedTermId
      ? treeQuery.isFetching && !indexTree
      : search.isFetching && hasQuery && results.length === 0;

  const referenceProps = {
    tab: referenceTab,
    onTabChange: setReferenceTab,
    keys: referenceKeys,
    description: tabular?.description ?? "",
    guidelines: cmGuidelines,
    guidelinesLoading: guidelineByCode.isFetching || guidelineByCategory.isFetching,
    guidelinesError: Boolean(guidelineByCode.isError || guidelineByCategory.isError),
    aha: cmAhaResults,
    ahaLoading: ahaByCode.isFetching || ahaByCategory.isFetching,
    ahaError: Boolean(ahaByCode.isError || ahaByCategory.isError),
    openGuidelineIds,
    onToggleGuideline: toggleGuideline,
    openAhaIds,
    onToggleAha: toggleAha,
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex min-h-0 flex-1 flex-col gap-1 p-1 xl:flex-row">
        <section className="flex min-h-[320px] min-w-0 flex-1 flex-col overflow-hidden rounded-lg border bg-white shadow-sm">
          <div
            ref={indexSearchRef}
            className="relative z-30 flex shrink-0 flex-wrap items-center gap-2 border-b bg-white px-3 py-1"
          >
            <h2 className="text-base font-semibold text-green-700">ICD-10-CM</h2>
            <select
              value={String(year)}
              onChange={(event) => {
                setYear(Number(event.target.value));
                clearSearch();
                resetTabularValidation();
              }}
              className="h-8 w-[100px] rounded-md border border-slate-300 bg-slate-50 px-2 text-sm font-medium text-slate-800"
            >
              {years.map((item) => (
                <option key={item} value={item}>
                  FY {item}
                </option>
              ))}
            </select>

            <div className="relative min-w-[200px] flex-1">
              <IconSearch
                size={14}
                className="pointer-events-none absolute left-2.5 top-2 text-slate-400"
              />
              <textarea
                ref={searchInputRef}
                value={searchInput}
                rows={1}
                onChange={(event) => {
                  setSearchInput(event.target.value);
                  setIndexMenuOpen(true);
                }}
                onFocus={() => setIndexMenuOpen(true)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") setIndexMenuOpen(false);
                  if (event.key === "Enter") {
                    event.preventDefault();
                    if (codedMatchesFirst && indexSuggestionHits[0]) {
                      selectCodedSuggestion(indexSuggestionHits[0]);
                    } else if (results[0]) {
                      selectTerm(results[0]);
                    }
                  }
                }}
                placeholder={searchPlaceholder}
                className="block h-8 min-h-8 w-full min-w-0 resize-none overflow-hidden rounded-md border border-slate-300 bg-white py-0 pl-8 pr-8 text-sm leading-8 outline-none"
              />
              {searchInput ? (
                <button
                  type="button"
                  onClick={clearSearch}
                  aria-label="Clear search"
                  className="absolute right-2 top-1.5 inline-flex h-5 w-5 items-center justify-center rounded text-slate-500 hover:bg-slate-100"
                >
                  <IconX size={14} />
                </button>
              ) : null}
            </div>

            <div className="ml-auto flex h-8 shrink-0 items-stretch overflow-hidden rounded-md border bg-slate-100">
              {CM_SOURCE_OPTIONS.map((opt, index) => (
                <div key={opt.value} className="flex items-stretch">
                  {index > 0 ? (
                    <span
                      aria-hidden
                      className="-my-0.5 w-px shrink-0 self-stretch bg-slate-300"
                    />
                  ) : null}
                  <button
                    type="button"
                    onClick={() => changeSource(opt.value)}
                    className={cn(
                      "inline-flex h-7 items-center rounded-[5px] px-2.5 text-[15px] font-medium transition-colors",
                      source === opt.value
                        ? "bg-slate-800 text-white shadow-sm"
                        : "text-slate-600 hover:bg-slate-200/70 hover:text-slate-900"
                    )}
                  >
                    {opt.label}
                  </button>
                </div>
              ))}
            </div>

              {indexMenuOpen && !tableMode && hasQuery ? (
                <div className="absolute left-0 right-0 top-full z-40 max-h-[min(40rem,70vh)] overflow-auto rounded-b-md border border-t-0 border-sky-200 bg-sky-50 shadow-lg">
                  {results.length === 0 &&
                  indexSuggestionHits.length === 0 &&
                  tabularNoteHits.length === 0 ? (
                    <p className="px-3 py-3 text-sm text-slate-600">
                      {search.isFetching
                        ? "Searching…"
                        : `No lead term matches “${query}”.`}
                    </p>
                  ) : (
                    <ul className="divide-y divide-sky-100">
                      {tabularNoteHits.map((hit: CmTabularNoteHit) => (
                        <li key={`note-${hit.id}`}>
                          <CodedTermSuggestion
                            hit={{
                              id: hit.id,
                              title: hit.title,
                              nemod: "",
                              code: hit.code,
                              path: [
                                hit.noteType === "includes"
                                  ? "Includes"
                                  : "Inclusion term",
                              ],
                            }}
                            words={highlightWords}
                            onSelect={() => selectTabularNote(hit)}
                          />
                        </li>
                      ))}
                      {codedMatchesFirst
                        ? indexSuggestionHits.map((hit) => (
                            <li key={`coded-${hit.id}-${hit.code}`}>
                              <CodedTermSuggestion
                                hit={hit}
                                words={highlightWords}
                                onSelect={() => selectCodedSuggestion(hit)}
                              />
                            </li>
                          ))
                        : null}
                      {results.map((hit: CmSearchHit) => {
                        const crossRef =
                          !hit.hasChildren && !hit.code
                            ? hit.see
                              ? { label: "see", target: hit.see }
                              : hit.seeAlso
                                ? { label: "see also", target: hit.seeAlso }
                                : null
                            : null;
                        if (crossRef && hit.redirectTargets.length > 0) {
                          return hit.redirectTargets.map((target: { title: string; nemod: string; code: string }) => (
                            <li key={`${hit.id}-${target.code}`}>
                              <CodedTermSuggestion
                                hit={{
                                  id: hit.id,
                                  title: target.title,
                                  nemod: target.nemod,
                                  code: target.code,
                                  path: [hit.title, crossRef.target],
                                }}
                                words={highlightWords}
                                onSelect={() => {
                                  followCrossRef(crossRef.label, crossRef.target);
                                  if (target.code) openTabular(target.code);
                                }}
                              />
                            </li>
                          ));
                        }
                        return (
                          <li key={hit.id}>
                            <button
                              type="button"
                              onClick={() => {
                                if (crossRef) {
                                  followCrossRef(crossRef.label, crossRef.target);
                                  return;
                                }
                                selectTerm(hit);
                              }}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-sky-100"
                            >
                              <span className="min-w-0 flex-1 text-[13px] leading-relaxed text-slate-900">
                                <HighlightedText
                                  text={
                                    hit.nemod
                                      ? `${hit.title} ${hit.nemod}`
                                      : hit.title
                                  }
                                  words={highlightWords}
                                />
                                {crossRef ? (
                                  <span className="text-slate-600">
                                    {" — "}
                                    {crossRef.label}{" "}
                                    <span className="text-blue-700">
                                      {crossRef.target}
                                    </span>
                                  </span>
                                ) : null}
                              </span>
                              {hit.code ? (
                                <span className="shrink-0 font-mono text-[13px] font-medium text-sky-700">
                                  {formatCmCode(hit.code)}
                                </span>
                              ) : null}
                            </button>
                          </li>
                        );
                      })}
                      {!codedMatchesFirst
                        ? indexSuggestionHits.map((hit) => (
                            <li key={`${hit.id}-${hit.code}`}>
                              <CodedTermSuggestion
                                hit={hit}
                                words={highlightWords}
                                onSelect={() => selectCodedSuggestion(hit)}
                              />
                            </li>
                          ))
                        : null}
                    </ul>
                  )}
                </div>
              ) : null}
          </div>

          {search.data?.expandedTerm && !tableMode ? (
            <p className="shrink-0 border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-sm text-amber-900">
              No exact match for “{query}” — searched the expansion “
              {search.data.expandedTerm}”.
            </p>
          ) : null}

          {!tableMode && selectedHit ? (
            <div className="shrink-0 bg-slate-50/60">
              <div className="flex flex-nowrap items-center gap-2 px-3 py-2">
                <div className="flex h-8 shrink-0 items-center gap-1">
                  <button
                    type="button"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 bg-white disabled:opacity-40"
                    aria-label="Back"
                    disabled={past.length === 0}
                    onClick={goIndexBack}
                  >
                    <IconArrowLeft size={14} />
                  </button>
                  <button
                    type="button"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 bg-white disabled:opacity-40"
                    aria-label="Forward"
                    disabled={future.length === 0}
                    onClick={goIndexForward}
                  >
                    <IconArrowRight size={14} />
                  </button>
                </div>
                <div className="relative flex h-8 min-w-0 max-w-[440px] flex-1 items-center">
                  <IconFilter
                    size={14}
                    className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    value={refineInput}
                    onChange={(event) => {
                      setRefineInput(event.target.value);
                      setRefineOpen(true);
                    }}
                    onFocus={() => setRefineOpen(true)}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") setRefineOpen(false);
                      if (event.key === "Enter" && deepSearchHits[0]) {
                        event.preventDefault();
                        selectDeepSearchHit(deepSearchHits[0]);
                      }
                    }}
                    placeholder="Deep search within this lead term (e.g. femur, open, initial)…"
                    className="h-8 w-full rounded-md border border-slate-300 bg-white py-0 pl-8 pr-7 text-sm outline-none"
                  />
                  {refineInput ? (
                    <button
                      type="button"
                      aria-label="Clear refine filter"
                      onClick={() => {
                        refineSelectionTextRef.current = null;
                        setRefineInput("");
                        setRefine("");
                        setRefineOpen(false);
                      }}
                      className="absolute right-1.5 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-slate-400 hover:bg-slate-100"
                    >
                      <IconX size={14} />
                    </button>
                  ) : null}
                  {refineOpen && refine.length > 0 ? (
                    <div className="absolute inset-x-0 top-full z-30 mt-1 max-h-[min(40rem,70vh)] overflow-auto rounded-md border border-sky-200 bg-sky-50 shadow-lg">
                      {deepSearchHits.length === 0 ? (
                        <p className="px-3 py-3 text-sm text-slate-600">
                          No coded terms match “{refine}” inside this lead term.
                        </p>
                      ) : (
                        <ul className="divide-y divide-sky-100">
                          {deepSearchHits.map((hit) => (
                            <li key={`${hit.id}-${hit.code}`}>
                              <CodedTermSuggestion
                                hit={hit}
                                words={filterWords}
                                onSelect={() => selectDeepSearchHit(hit)}
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : null}
                </div>
                <AlphabetJumpBar letters={sectionLetters} onJump={jumpToLetter} />
              </div>
            </div>
          ) : null}

          <div ref={treeScrollRef} className="min-h-0 flex-1 overflow-auto">
            {leftPanelLoading ? (
              <div className="flex h-full min-h-[240px] flex-col items-center justify-center gap-2 px-4 py-8 text-center">
                <Spinner
                  label={
                    tableMode
                      ? source === "drugs"
                        ? "Loading drugs table…"
                        : "Loading neoplasms table…"
                      : selectedTermId
                        ? "Loading index…"
                        : "Searching…"
                  }
                />
              </div>
            ) : (
              <>
                {search.isError && !tableMode ? (
                  <p className="m-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
                    Search failed — confirm ICD-10-CM data is ingested for {year}.
                  </p>
                ) : null}
                {tableMode && tableQuery.isError ? (
                  <p className="m-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
                    Table failed to load — confirm ICD-10-CM data is ingested for{" "}
                    {year}.
                  </p>
                ) : null}
                {!tableMode && !hasQuery && !search.isError ? (
                  <CmEmptyState
                    title="Start with a lead term"
                    hint="Type at least 2 characters. Every match for exactly what you type is listed, so you choose the lead term."
                  />
                ) : null}
                {!tableMode &&
                hasQuery &&
                !search.isFetching &&
                results.length === 0 &&
                !search.isError &&
                !indexTree ? (
                  <CmEmptyState
                    title={`No lead term matches “${query}”`}
                    hint="Try a shorter term, or switch the index above (Diseases, External Cause, Drugs, Neoplasms)."
                  />
                ) : null}
                {!tableMode && indexTree ? (
                  <IndexTreeView
                    node={indexTree}
                    onCode={(code) => openTabular(code)}
                    onCrossRef={followCrossRef}
                    expand={expand}
                    onToggle={toggleNode}
                    activeCode={tabularCode}
                    highlightWords={highlightWords}
                    seventhCharCodes={seventhCharCodes}
                    jumpSectionId={jumpSectionId}
                    jumpedTermId={jumpedIndexTermId}
                  />
                ) : null}
                {tableMode && tableTrees.length === 0 && !tableQuery.isFetching ? (
                  <CmEmptyState
                    title="No table rows match"
                    hint="Search a substance or site name, or clear the search box to browse the table."
                  />
                ) : null}
                {tableMode && tableTrees.length > 0 ? (
                  <TableTreeView
                    trees={tableTrees}
                    source={tableSource}
                    onCode={(code) => openTabular(code)}
                    onCrossRef={followCrossRef}
                    expand={expand}
                    onToggle={toggleNode}
                    activeCode={tabularCode}
                    highlightWords={highlightWords}
                    seventhCharCodes={seventhCharCodes}
                    jumpedTermId={null}
                    footnote={
                      tableQuery.data &&
                      tableQuery.data.total > tableQuery.data.trees.length
                        ? `Showing the first ${tableQuery.data.trees.length} of ${tableQuery.data.total} substances — narrow the search to see more.`
                        : null
                    }
                  />
                ) : null}
              </>
            )}
          </div>
        </section>

        <section className="flex min-h-[320px] min-w-0 flex-1 flex-col overflow-hidden rounded-lg border bg-white shadow-sm">
          <TabularListSearch
            mode={tabularSearchMode}
            onModeChange={(mode) => {
              setTabularSearchMode(mode);
              setTabularSearchOpen(true);
            }}
            value={tabularSearchInput}
            onValueChange={(value) => {
              setTabularSearchInput(value);
              setTabularSearchOpen(true);
            }}
            open={tabularSearchOpen}
            onOpenChange={setTabularSearchOpen}
            results={tabularSearch.data ?? []}
            loading={tabularSearch.isFetching}
            activeCode={tabularCode}
            minLength={tabularMinLength}
            onSelect={(code) => openTabular(code, { fromTabularSearch: true })}
            onReset={resetTabularValidation}
          />
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <TabularPanel
              tabular={tabular}
              requestedCode={requestedCode}
              year={year}
              loading={Boolean(tabularCode) && tabularQuery.isFetching && !tabular}
              selectedSeventh={selectedSeventh}
              onSelectSeventh={setSelectedSeventh}
              onOpenCode={(code) => openTabular(code)}
              finalizedCode={finalizedCode}
              canCopyCode={canCopyCode}
              canGoBack={tabularPast.length > 0}
              onBack={goTabularBack}
              onConfirmCode={
                onConfirmCode && canCopyCode && finalizedCode
                  ? () =>
                      onConfirmCode({
                        code: finalizedCode,
                        description: tabular?.description ?? "",
                      })
                  : undefined
              }
            />
          </div>
          <CmCodingReferencePanel
            {...referenceProps}
            onExpand={() => setReferenceOpen(true)}
          />
        </section>
      </div>
      <CmCodingReferenceDialog
        {...referenceProps}
        open={referenceOpen}
        onClose={() => setReferenceOpen(false)}
      />
    </div>
  );
};

export default CmBuilderTab;
